import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../hooks/useApi';
import { formatDate, formatDateTime } from '../../utils/format';
import {
  ROSTER_FILTERS, STATUS_LABEL, STOCK_LABEL, STOCK_STATES, UNASSIGNED_FILTERS,
  assigneeGroups, displayName, filterRoster, filterUnassigned, pickerGroups, syncLine, unassignedOrigin,
} from '../../utils/laptops';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { LAPTOPS_TOUR_ID, laptopsTourSteps } from './laptopsTour';

// Laptops: everyone on Ops with their assigned laptop, checked against PDQ
// Connect. PDQ suggests the laptop a person signs in to; somebody accepts it.
// Every write answers with the refreshed roster, and its result is reported
// next to the control that was used.

const STATUS_TINT = {
  assigned: 'bg-emerald-50 text-emerald-700',
  suggested: 'bg-blue-50 text-blue-700',
  missing: 'bg-amber-100 text-amber-700',
  exempt: 'bg-gray-100 text-gray-600',
};

function StatusTag({ status }) {
  return <span className={`inline-flex px-1.5 py-px rounded-md text-[9px] font-semibold ${STATUS_TINT[status] || ''}`}>{STATUS_LABEL[status] || status}</span>;
}

function Note({ note }) {
  if (!note) return null;
  return <div role="status" className={`text-[9px] mt-1 ${note.kind === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}>{note.text}</div>;
}

function DeviceLine({ d }) {
  if (!d) return null;
  return (
    <div className="text-[9px] text-gray-500">
      {[d.model, d.serial_number && `S/N ${d.serial_number}`].filter(Boolean).join(' · ')}
      <div>
        {d.gone ? 'No longer in PDQ' : (d.last_checkin_at ? `Seen by PDQ ${formatDateTime(d.last_checkin_at)}` : 'Not yet seen by PDQ')}
        {d.online ? ' · online now' : ''}
        {d.last_user ? ` · last user ${d.last_user}` : ''}
      </div>
    </div>
  );
}

function PersonPanel({ person, devices, onClose, onRoster }) {
  const [history, setHistory] = useState(null);
  const [choice, setChoice] = useState('');      // a PDQ device id
  const [typed, setTyped] = useState('');        // or an asset tag typed in
  const [clash, setClash] = useState(null);      // { asset_tag, holder } when the laptop is somebody else's
  const [confirmReturn, setConfirmReturn] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const loadHistory = useCallback(() => {
    api.get(`laptops/${person.id}/history`).then(setHistory).catch(() => setHistory([]));
  }, [person.id]);
  useEffect(() => { setHistory(null); loadHistory(); }, [loadHistory]);

  const groups = useMemo(() => pickerGroups(devices, person.id), [devices, person.id]);
  const chosen = devices.find(d => d.id === choice) || null;
  // The screen already knows when the chosen laptop is somebody else's, so it
  // asks before assigning rather than reporting a refusal afterwards.
  const knownClash = chosen?.holder && chosen.holder.id !== person.id ? { asset_tag: chosen.name, holder: chosen.holder } : null;
  const shownClash = knownClash || clash;

  const run = async (work, ok) => {
    setBusy(true); setNote(null);
    try {
      const res = await work();
      onRoster(res.roster);
      setNote({ kind: 'ok', text: ok(res.result) });
      setChoice(''); setTyped(''); setClash(null); setConfirmReturn(false); setReason('');
      loadHistory();
    } catch (e) {
      if (e.data?.code === 'held') setClash({ asset_tag: e.data.asset_tag, holder: e.data.holder });
      else setNote({ kind: 'error', text: e.message || 'That could not be saved.' });
    } finally {
      setBusy(false);
    }
  };

  const assign = (input, take_over = false) => run(
    () => api.post(`laptops/${person.id}/assign`, { ...input, take_over }),
    r => `${r.asset_tag} is now ${displayName(person)}'s laptop.${r.replaced ? ` ${r.replaced} is recorded as returned.` : ''}`,
  );
  const assignChosen = (take_over = false) => {
    if (chosen) return assign({ pdq_device_id: chosen.id }, take_over);
    if (typed.trim()) return assign({ asset_tag: typed.trim() }, take_over);
    setNote({ kind: 'error', text: 'Choose a laptop from PDQ or type its asset tag.' });
  };

  const name = displayName(person);
  const l = person.laptop;

  return (
    <aside data-tour="person-panel" className="bg-white border border-gray-200 rounded p-3 w-full lg:w-[340px] lg:shrink-0 self-start">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-[12px] font-bold text-navy">{name}</div>
          <div className="text-[9px] text-gray-500">{[person.designation, person.team_name].filter(Boolean).join(' · ') || ' '}</div>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-[14px] leading-none">×</button>
      </div>

      <div className="border border-gray-200 rounded p-2 mb-3 bg-gray-50">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] uppercase font-bold text-gray-500">Laptop</span>
          <StatusTag status={person.status} />
        </div>
        {l ? (
          <>
            <div className="text-[12px] font-bold text-navy">{l.asset_tag}</div>
            {l.device ? <DeviceLine d={l.device} /> : <div className="text-[9px] text-gray-500">{[l.model, l.serial_number && `S/N ${l.serial_number}`].filter(Boolean).join(' · ') || 'Not in PDQ Connect'}</div>}
            <div className="text-[9px] text-gray-400 mt-0.5">Assigned {formatDate(l.assigned_date)}{l.assigned_by ? ` by ${l.assigned_by}` : ''}</div>
          </>
        ) : person.status === 'exempt' ? (
          <div className="text-[9px] text-gray-600">Does not need a laptop: {person.exempt_reason}</div>
        ) : (
          <div className="text-[9px] text-gray-600">No laptop assigned.</div>
        )}
        {person.flags.map((f, i) => <div key={i} className="text-[9px] text-red-600 mt-1">{f.text}</div>)}
        {person.others.length > 0 && (
          <div className="text-[9px] text-gray-500 mt-1">Also signs in to {person.others.map(d => d.name).join(', ')}.</div>
        )}
      </div>

      {person.suggestion && (
        <div data-tour="suggestion" className="border border-blue-200 rounded p-2 mb-3 bg-blue-50">
          <div className="text-[9px] uppercase font-bold text-blue-700 mb-0.5">PDQ suggests {person.suggestion.name}</div>
          <DeviceLine d={person.suggestion} />
          <button onClick={() => assign({ pdq_device_id: person.suggestion.id })} disabled={busy}
            className="mt-1.5 px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">
            Assign {person.suggestion.name}
          </button>
        </div>
      )}

      <div data-tour="assign" className="mb-3">
        <label className="block text-[9px] uppercase font-bold text-gray-500 mb-1" htmlFor="laptop-choice">{l ? 'Change laptop' : 'Assign a laptop'}</label>
        <select id="laptop-choice" className="form-select mb-1" value={choice} disabled={busy}
          onChange={e => { setChoice(e.target.value); setTyped(''); setClash(null); setNote(null); }}>
          <option value="">{groups.length ? 'Choose from PDQ Connect' : 'No PDQ laptops to choose from'}</option>
          {groups.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.devices.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.model ? ` · ${d.model}` : ''}{d.holder ? ` · ${d.holder.name}` : (d.user ? ` · used by ${d.user.name}` : '')}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <input className="form-input mb-1" placeholder="or type an asset tag, e.g. AV-10066" value={typed} disabled={busy}
          onChange={e => { setTyped(e.target.value); setChoice(''); setClash(null); setNote(null); }} />

        {shownClash ? (
          <div className="border border-amber-300 bg-amber-50 rounded p-2">
            <div className="text-[10px] font-semibold text-amber-800">Move {shownClash.asset_tag} to {name}?</div>
            <div className="text-[9px] text-amber-800 mb-1.5">
              It is assigned to {shownClash.holder?.name || 'somebody else'}. Moving it records it as returned by them{shownClash.holder ? `, and leaves ${shownClash.holder.name} with no laptop` : ''}.
            </div>
            <div className="flex gap-1">
              <button onClick={() => assignChosen(true)} disabled={busy}
                className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">Move laptop</button>
              <button onClick={() => { setChoice(''); setTyped(''); setClash(null); }} disabled={busy}
                className="px-2 py-1 rounded text-[10px] border border-gray-200 bg-white text-gray-700">
                Keep with {shownClash.holder?.name || 'them'}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => assignChosen(false)} disabled={busy || (!choice && !typed.trim())}
            className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">
            {busy ? 'Saving…' : (l ? 'Change laptop' : 'Assign laptop')}
          </button>
        )}
        {l && (choice || typed.trim()) && !shownClash && (
          <div className="text-[9px] text-gray-500 mt-1">{l.asset_tag} will be recorded as returned.</div>
        )}
      </div>

      {l && (
        <div className="mb-3">
          {confirmReturn ? (
            <div className="border border-gray-200 rounded p-2">
              <div className="text-[10px] font-semibold text-navy mb-1">Record {l.asset_tag} as handed back?</div>
              <div className="text-[9px] text-gray-500 mb-1.5">{name} will show with no laptop.</div>
              <div className="flex gap-1">
                <button onClick={() => run(() => api.post(`laptops/${person.id}/return`, {}), r => `${r.asset_tag} is recorded as returned.`)} disabled={busy}
                  className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">Record as returned</button>
                <button onClick={() => setConfirmReturn(false)} disabled={busy}
                  className="px-2 py-1 rounded text-[10px] border border-gray-200 bg-white text-gray-700">Keep {l.asset_tag} with {name}</button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setConfirmReturn(true); setNote(null); }} className="text-[10px] text-gray-600 underline">Laptop handed back</button>
          )}
        </div>
      )}

      {!l && (
        <div data-tour="exempt" className="mb-3">
          {person.status === 'exempt' ? (
            <button onClick={() => run(() => api.put(`laptops/${person.id}/exempt`, { reason: '' }), () => `${name} is back on the list of people who need a laptop.`)} disabled={busy}
              className="text-[10px] text-gray-600 underline">They need a laptop after all</button>
          ) : (
            <>
              <label className="block text-[9px] uppercase font-bold text-gray-500 mb-1" htmlFor="laptop-exempt">No laptop needed</label>
              <div className="flex gap-1">
                <input id="laptop-exempt" className="form-input" placeholder="Why, e.g. contractor on own kit" value={reason} disabled={busy}
                  onChange={e => { setReason(e.target.value); setNote(null); }} />
                <button onClick={() => run(() => api.put(`laptops/${person.id}/exempt`, { reason }), () => `${name} is marked as not needing a laptop.`)}
                  disabled={busy || !reason.trim()}
                  className="px-2 py-1 rounded text-[10px] font-semibold border border-gray-200 bg-white text-gray-700 disabled:opacity-40 whitespace-nowrap">Save</button>
              </div>
            </>
          )}
        </div>
      )}

      <Note note={note} />

      <div className="text-[9px] uppercase font-bold text-gray-500 mb-1 mt-2">History</div>
      {history === null && <div className="text-[9px] text-gray-400">Loading…</div>}
      {history && history.length === 0 && <div className="text-[9px] text-gray-500">No laptop recorded on Ops yet.</div>}
      {history && history.length > 0 && (
        <ol className="divide-y divide-gray-100">
          {history.map(h => (
            <li key={h.id} className="py-1.5 text-[9px] text-gray-600">
              <span className="font-semibold text-navy text-[10px]">{h.asset_tag}</span>
              {' '}assigned {formatDate(h.assigned_date)}{h.assigned_by ? ` by ${h.assigned_by}` : ''}
              {h.returned_date ? `, returned ${formatDate(h.returned_date)}${h.returned_by ? ` (${h.returned_by})` : ''}` : ', current'}
              {h.notes && <div className="text-gray-400 whitespace-pre-line">{h.notes}</div>}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

// One unassigned laptop: what it is waiting for, and giving it to somebody.
// Its result shows on the row; a row that leaves the current filter reports
// at the top of the section instead.
function UnassignedRow({ u, people, open, onOpen, onClose, onRoster, onNote }) {
  const [personId, setPersonId] = useState('');
  const [note, setNoteText] = useState(u.note || '');
  const [busy, setBusy] = useState(false);
  const groups = useMemo(() => assigneeGroups(people), [people]);
  const to = people.find(p => p.id === personId) || null;

  const run = async (work, ok) => {
    setBusy(true);
    try {
      const res = await work();
      onRoster(res.roster);
      onNote({ key: u.asset_tag, kind: 'ok', text: ok });
      setPersonId('');
      onClose();
    } catch (e) {
      onNote({ key: u.asset_tag, kind: 'error', text: e.message || 'That could not be saved.' });
    } finally {
      setBusy(false);
    }
  };

  const setState = (state) => run(
    () => api.put('laptops/stock', { asset_tag: u.asset_tag, state }),
    `${u.asset_tag} is ${STOCK_LABEL[state].toLowerCase()}.`,
  );
  const saveNote = () => run(
    () => api.put('laptops/stock', { asset_tag: u.asset_tag, state: u.state === 'unsorted' ? 'spare' : u.state, note }),
    u.state === 'unsorted' ? `Note saved, and ${u.asset_tag} is marked as spare.` : 'Note saved.',
  );
  const giveOut = () => run(
    () => api.post(`laptops/${to.id}/assign`, { asset_tag: u.asset_tag }),
    `${u.asset_tag} is now ${displayName(to)}'s laptop.${to.laptop ? ` ${to.laptop.asset_tag} is recorded as returned.` : ''}`,
  );

  const d = u.device;
  return (
    <>
      <tr className={`border-b border-gray-100 ${open ? 'bg-gold/10' : ''}`}>
        <td className="px-2 py-1 text-[10px] font-semibold text-navy whitespace-nowrap">{u.asset_tag}</td>
        <td className="px-2 py-1 text-[9px] text-gray-600">
          {u.model || '-'}
          {u.serial_number && <div className="text-gray-400">S/N {u.serial_number}</div>}
        </td>
        <td className="px-2 py-1 text-[9px] text-gray-500">
          {unassignedOrigin(u, formatDate)}
          {d?.gone && <div>No longer in PDQ</div>}
          {u.note && <div className="text-gray-400 whitespace-pre-line">{u.note}</div>}
        </td>
        <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{d?.last_checkin_at ? formatDate(d.last_checkin_at) : '-'}</td>
        <td className="px-2 py-1">
          <select aria-label={`State of ${u.asset_tag}`} className="form-select text-[10px] py-0.5" value={u.state} disabled={busy}
            onChange={e => setState(e.target.value)}>
            {STOCK_STATES.filter(s => s !== 'unsorted' || u.source !== 'added' || u.state === 'unsorted').map(s => (
              <option key={s} value={s}>{STOCK_LABEL[s]}</option>
            ))}
          </select>
        </td>
        <td className="px-2 py-1 text-right whitespace-nowrap">
          {u.state !== 'retired' && (
            <button onClick={open ? onClose : onOpen} disabled={busy}
              className="px-2 py-1 rounded text-[10px] border border-gray-200 bg-white text-gray-700 disabled:opacity-40">
              {open ? 'Close' : 'Give out'}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-gray-100 bg-gray-50">
          <td colSpan={6} className="px-2 py-2">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-[9px] uppercase font-bold text-gray-500 mb-1" htmlFor={`give-${u.asset_tag}`}>Give {u.asset_tag} to</label>
                <select id={`give-${u.asset_tag}`} className="form-select mb-1" value={personId} disabled={busy}
                  onChange={e => setPersonId(e.target.value)}>
                  <option value="">Choose a person</option>
                  {groups.map(g => (
                    <optgroup key={g.label} label={g.label}>
                      {g.people.map(p => (
                        <option key={p.id} value={p.id}>
                          {displayName(p)}{p.laptop ? ` · has ${p.laptop.asset_tag}` : ''}{p.status === 'suggested' ? ` · PDQ suggests ${p.suggestion.name}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {to?.laptop && <div className="text-[9px] text-gray-500 mb-1">{to.laptop.asset_tag} will be recorded as returned by {displayName(to)}.</div>}
                {to?.status === 'exempt' && <div className="text-[9px] text-gray-500 mb-1">{displayName(to)} is marked as not needing a laptop; this clears it.</div>}
                <button onClick={giveOut} disabled={busy || !to}
                  className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">
                  {busy ? 'Saving…' : (to ? `Give to ${displayName(to)}` : 'Give out')}
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-[9px] uppercase font-bold text-gray-500 mb-1" htmlFor={`note-${u.asset_tag}`}>Note</label>
                <textarea id={`note-${u.asset_tag}`} rows={2} className="form-input mb-1" value={note} disabled={busy}
                  placeholder="e.g. at Dell for a new keyboard, or wiped and in the cupboard"
                  onChange={e => setNoteText(e.target.value)} />
                <button onClick={saveNote} disabled={busy || note === (u.note || '')}
                  className="px-2 py-1 rounded text-[10px] border border-gray-200 bg-white text-gray-700 disabled:opacity-40">Save note</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function UnassignedLaptops({ data, onRoster, sectionRef }) {
  const [filter, setFilter] = useState('open');
  const [openTag, setOpenTag] = useState(null);
  const [note, setNote] = useState(null);       // { key, kind, text }
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ asset_tag: '', model: '', serial_number: '' });
  const [busy, setBusy] = useState(false);

  const all = data?.unassigned || [];
  const rows = filterUnassigned(all, filter);
  const counts = data?.summary?.unassigned || {};
  const people = data?.people || [];
  // A row that has just left the filter (retired, or given out) reports here.
  const orphan = note && !rows.some(u => u.asset_tag === note.key) ? note : null;

  const add = async () => {
    setBusy(true); setNote(null);
    try {
      const res = await api.put('laptops/stock', { ...draft, state: 'spare' });
      onRoster(res.roster);
      setNote({ key: 'add', kind: 'ok', text: `${res.result.asset_tag} is added as a spare.` });
      setDraft({ asset_tag: '', model: '', serial_number: '' });
      setAdding(false);
      setFilter('open');
    } catch (e) {
      setNote({ key: 'add', kind: 'error', text: e.message || 'That could not be saved.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={sectionRef} data-tour="unassigned" className="bg-white border border-gray-200 rounded mt-3">
      <div className="px-2 py-1.5 border-b border-gray-200 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold text-navy">Unassigned laptops</span>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
          {UNASSIGNED_FILTERS.map(f => {
            const n = f.id === 'open' ? (counts.unsorted || 0) + (counts.spare || 0) + (counts.repair || 0) : (counts[f.id] || 0);
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-2 py-1 text-[10px] ${filter === f.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {f.label} {n}
              </button>
            );
          })}
        </div>
        <button onClick={() => { setAdding(!adding); setNote(null); }}
          className="ml-auto px-2 py-1 rounded text-[10px] border border-gray-200 bg-white text-gray-700">
          {adding ? 'Close' : 'Add a laptop'}
        </button>
      </div>

      {adding && (
        <div className="px-2 py-2 border-b border-gray-200 bg-gray-50">
          <div className="text-[9px] text-gray-500 mb-1">For a laptop PDQ does not know yet, such as one just bought. It is added as a spare. Name the laptop with this tag when you build it, and the row links to PDQ once it enrols (by serial too, if it enrols under another name).</div>
          <div className="flex flex-wrap gap-1">
            <input className="form-input max-w-[160px]" placeholder="Asset tag, e.g. AV-10180" value={draft.asset_tag} disabled={busy}
              onChange={e => setDraft({ ...draft, asset_tag: e.target.value })} />
            <input className="form-input max-w-[180px]" placeholder="Model (optional)" value={draft.model} disabled={busy}
              onChange={e => setDraft({ ...draft, model: e.target.value })} />
            <input className="form-input max-w-[160px]" placeholder="Serial (optional)" value={draft.serial_number} disabled={busy}
              onChange={e => setDraft({ ...draft, serial_number: e.target.value })} />
            <button onClick={add} disabled={busy || !draft.asset_tag.trim()}
              className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">{busy ? 'Saving…' : 'Add as spare'}</button>
          </div>
          {note?.key === 'add' && note.kind === 'error' && <Note note={note} />}
        </div>
      )}

      {orphan && !(orphan.key === 'add' && orphan.kind === 'error') && <div className="px-2 pt-1.5"><Note note={orphan} /></div>}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            {['Laptop', 'Model', 'Where from', 'Seen by PDQ', 'State', ''].map((h, i) => (
              <th key={i} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {!data && <tr><td colSpan={6} className="px-2 py-3 text-[10px] text-gray-400">Loading…</td></tr>}
            {data && rows.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-3 text-[10px] text-gray-500">
                {filter === 'open' ? 'Every laptop Ops knows of is with somebody.' : `No laptops are ${STOCK_LABEL[filter].toLowerCase()}.`}
              </td></tr>
            )}
            {rows.map(u => (
              <UnassignedRowWithNote key={u.asset_tag} u={u} people={people} note={note}
                open={openTag === u.asset_tag} onOpen={() => { setOpenTag(u.asset_tag); setNote(null); }} onClose={() => setOpenTag(null)}
                onRoster={onRoster} onNote={setNote} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnassignedRowWithNote({ note, ...props }) {
  return (
    <>
      <UnassignedRow {...props} />
      {note?.key === props.u.asset_tag && (
        <tr className="border-b border-gray-100"><td colSpan={6} className="px-2 pb-1.5"><Note note={note} /></td></tr>
      )}
    </>
  );
}

export default function LaptopsPage({ initialPersonId = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Opened from a person's profile (People → Laptop tab), the page opens on them.
  const [selectedId, setSelectedId] = useState(initialPersonId);
  useEffect(() => { if (initialPersonId) setSelectedId(initialPersonId); }, [initialPersonId]);
  const [busy, setBusy] = useState(null);         // 'sync' | 'accept'
  const [syncNote, setSyncNote] = useState(null);
  const [acceptNote, setAcceptNote] = useState(null);
  const [recoverNote, setRecoverNote] = useState(null); // { key, kind, text }
  const recoverRef = useRef(null);
  const unassignedRef = useRef(null);

  const load = useCallback(() => {
    api.get('laptops').then(d => { setData(d); setError(null); }).catch(e => setError(e.message || 'Could not load laptops.'));
  }, []);
  useEffect(load, [load]);

  const tour = useFeatureTour(LAPTOPS_TOUR_ID, { enabled: !!data });

  const people = data?.people || [];
  const rows = useMemo(() => filterRoster(people, filter, search), [people, filter, search]);
  const selected = people.find(p => p.id === selectedId) || null;
  const s = data?.summary;
  const sync = syncLine(data?.pdq);
  const noLaptop = s ? s.missing + s.suggested : 0;

  const syncNow = async () => {
    setBusy('sync'); setSyncNote(null);
    try {
      const res = await api.post('laptops/sync', {});
      setData(res.roster);
      setSyncNote(res.result.ok
        ? { kind: 'ok', text: `Synced: ${res.result.devices} devices${res.result.gone ? `, ${res.result.gone} no longer in PDQ` : ''}.` }
        : { kind: 'error', text: `PDQ did not answer: ${res.result.error}` });
    } catch (e) {
      setSyncNote({ kind: 'error', text: e.message || 'The sync failed.' });
    } finally {
      setBusy(null);
    }
  };

  const acceptAll = async () => {
    setBusy('accept'); setAcceptNote(null);
    try {
      const res = await api.post('laptops/accept-suggestions', {});
      setData(res.roster);
      const { assigned, skipped } = res.result;
      setAcceptNote({ kind: skipped.length ? 'error' : 'ok',
        text: `${assigned.length} laptop${assigned.length === 1 ? '' : 's'} assigned.${skipped.length ? ` ${skipped.length} skipped: ${skipped.map(x => x.reason).join('; ')}` : ''}` });
    } catch (e) {
      setAcceptNote({ kind: 'error', text: e.message || 'The suggestions could not be accepted.' });
    } finally {
      setBusy(null);
    }
  };

  const recovered = async (r, key) => {
    setBusy(key); setRecoverNote(null);
    try {
      const res = await api.post(`laptops/${r.person_id}/return`, { note: 'Recovered from a leaver' });
      setData(res.roster);
      setRecoverNote({ key: 'done', kind: 'ok', text: `${r.asset_tag} is recorded as returned by ${r.person_name}.` });
    } catch (e) {
      setRecoverNote({ key, kind: 'error', text: e.message || 'That could not be saved.' });
    } finally {
      setBusy(null);
    }
  };

  const tile = (id, n, label, tone) => (
    <button onClick={() => setFilter(filter === id ? 'all' : id)}
      className={`px-2 py-1 rounded border text-[10px] font-semibold flex items-center gap-1.5 ${n ? tone : 'border-gray-200 bg-white text-gray-600'} ${filter === id ? 'ring-2 ring-gold/40' : ''}`}>
      <span className="text-[13px] font-black leading-none">{data ? n : '–'}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <div className="p-4 fade-in">
      <div data-tour="page-header" className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h1 className="text-[15px] font-black text-navy flex items-center gap-2">
            Laptops
            <button onClick={tour.start} title="Replay the guided tour" aria-label="Replay the guided tour"
              className="w-4 h-4 rounded-full border border-gray-300 text-[9px] text-gray-500 hover:bg-gray-100 leading-none">?</button>
          </h1>
          <div className="text-[9px] text-gray-500 mt-0.5">Everyone on Ops with their assigned laptop, checked against PDQ Connect.</div>
        </div>
        <div data-tour="pdq-sync" className="text-right max-w-[360px]">
          <div className={`text-[9px] ${sync.kind === 'error' ? 'text-red-600' : 'text-gray-500'}`}>
            {sync.text}{sync.at ? `, ${formatDateTime(sync.at)}` : ''}
          </div>
          {data?.pdq?.configured && (
            <button onClick={syncNow} disabled={busy === 'sync'}
              className="mt-1 px-2 py-1 rounded text-[10px] font-semibold border border-gray-200 bg-white text-gray-700 disabled:opacity-40">
              {busy === 'sync' ? 'Syncing…' : 'Sync with PDQ now'}
            </button>
          )}
          <Note note={syncNote} />
        </div>
      </div>

      {error && <div className="text-[10px] text-red-600 mb-2">{error}</div>}

      <div data-tour="tiles" className="flex items-center gap-2 mb-2 flex-wrap">
        {tile('missing', noLaptop, noLaptop === 1 ? 'person with no laptop' : 'people with no laptop', 'border-amber-300 bg-amber-50 text-amber-800')}
        {tile('suggested', s?.suggested || 0, 'suggested by PDQ', 'border-blue-200 bg-blue-50 text-blue-700')}
        {tile('flagged', s?.flagged || 0, 'need a look', 'border-red-200 bg-red-50 text-red-700')}
        {s?.unassigned && (
          <button onClick={() => unassignedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-2 py-1 rounded border text-[10px] font-semibold flex items-center gap-1.5 border-gray-200 bg-white text-gray-600">
            <span className="text-[13px] font-black leading-none">{s.unassigned.unsorted + s.unassigned.spare + s.unassigned.repair}</span>
            <span>unassigned{s.unassigned.spare ? `, ${s.unassigned.spare} spare` : ''}</span>
          </button>
        )}
        {(s?.to_recover || 0) > 0 && (
          <button onClick={() => recoverRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-2 py-1 rounded border text-[10px] font-semibold flex items-center gap-1.5 border-red-200 bg-red-50 text-red-700">
            <span className="text-[13px] font-black leading-none">{s.to_recover}</span>
            <span>with leavers</span>
          </button>
        )}
        {s?.suggested > 0 && (
          <div data-tour="accept-all" className="flex flex-col">
            <button onClick={acceptAll} disabled={busy === 'accept'}
              className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40">
              {busy === 'accept' ? 'Assigning…' : `Accept all ${s.suggested} suggestion${s.suggested === 1 ? '' : 's'}`}
            </button>
          </div>
        )}
        <Note note={acceptNote} />
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div data-tour="filters" className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
          {ROSTER_FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2 py-1 text-[10px] ${filter === f.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <input className="form-input max-w-[240px]" placeholder="Search name, team, asset tag or serial" value={search} onChange={e => setSearch(e.target.value)} />
        <span className="text-[9px] text-gray-500 ml-auto">{rows.length} of {people.length}</span>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 items-start">
        <div data-tour="roster" className="bg-white border border-gray-200 rounded overflow-x-auto flex-1 min-w-0 w-full">
          <table className="w-full">
            <thead><tr>
              {['Person', 'Laptop', 'Model', 'Seen by PDQ', 'Status'].map(h => (
                <th key={h} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {!data && !error && <tr><td colSpan={5} className="px-2 py-3 text-[10px] text-gray-400">Loading…</td></tr>}
              {data && rows.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-3 text-[10px] text-gray-500">
                  {filter === 'missing' && !search ? 'Everyone has a laptop.' : 'Nobody matches.'}
                </td></tr>
              )}
              {rows.map(p => {
                const d = p.laptop?.device || p.suggestion;
                return (
                  <tr key={p.id} onClick={() => setSelectedId(p.id)}
                    className={`cursor-pointer hover:bg-gold/5 ${selectedId === p.id ? 'bg-gold/10' : ''}`}>
                    <td className="px-2 py-1 text-[10px] font-semibold text-navy whitespace-nowrap">
                      {displayName(p)}
                      {p.team_name && <div className="text-[8px] text-gray-400 font-normal">{p.team_name}</div>}
                    </td>
                    <td className="px-2 py-1 text-[10px] whitespace-nowrap">
                      {p.laptop ? <span className="font-semibold text-navy">{p.laptop.asset_tag}</span>
                        : p.suggestion ? <span className="text-blue-700">{p.suggestion.name}?</span>
                        : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-2 py-1 text-[9px] text-gray-600">{p.laptop?.model || d?.model || '-'}</td>
                    <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{d?.last_checkin_at ? formatDate(d.last_checkin_at) : '-'}</td>
                    <td className="px-2 py-1">
                      <StatusTag status={p.status} />
                      {p.flags.map((f, i) => <div key={i} className="text-[8px] text-red-600">{f.text}</div>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected && (
          <PersonPanel key={selected.id} person={selected} devices={data?.devices || []}
            onClose={() => setSelectedId(null)} onRoster={setData} />
        )}
      </div>

      <UnassignedLaptops data={data} onRoster={setData} sectionRef={unassignedRef} />

      {data?.to_recover?.length > 0 && (
        <div ref={recoverRef} data-tour="recover" className="bg-white border border-gray-200 rounded mt-3">
          <div className="px-2 py-1.5 border-b border-gray-200 text-[10px] font-bold text-navy">Laptops with people who have left</div>
          <table className="w-full">
            <tbody>
              {data.to_recover.map((r, i) => {
                const key = `recover-${r.asset_tag}-${i}`;
                return (
                  <tr key={key} className="border-b border-gray-100 last:border-0">
                    <td className="px-2 py-1 text-[10px] font-semibold text-navy whitespace-nowrap">{r.asset_tag}</td>
                    <td className="px-2 py-1 text-[10px] text-gray-700">{r.person_name}</td>
                    <td className="px-2 py-1 text-[9px] text-gray-500">
                      {r.source === 'assigned' ? 'Still assigned to them on Ops' : 'Last signed in to by them, per PDQ. Assign it to somebody once it is back.'}
                      {r.device?.last_checkin_at ? ` Seen by PDQ ${formatDate(r.device.last_checkin_at)}.` : ''}
                      {recoverNote?.key === key && <Note note={recoverNote} />}
                    </td>
                    <td className="px-2 py-1 text-right">
                      {r.source === 'assigned' && (
                        <button onClick={() => recovered(r, key)} disabled={busy === key}
                          className="px-2 py-1 rounded text-[10px] border border-gray-200 bg-white text-gray-700 disabled:opacity-40 whitespace-nowrap">
                          {busy === key ? 'Saving…' : 'Record as returned'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {recoverNote?.key === 'done' && <div className="px-2 pb-1.5"><Note note={recoverNote} /></div>}
        </div>
      )}
      {!data?.to_recover?.length && recoverNote?.key === 'done' && <div className="mt-2"><Note note={recoverNote} /></div>}

      {tour.open && <FeatureTour steps={laptopsTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
